<template>
  <div class="web-promo-card-wrapper" :class="css">
    <div
      class="web-promo-card"
      :class="[
        cardCss,
        {
          'web-promo-card--clickable': isCardClickable,
          'web-promo-card--image': hasImage,
        },
      ]"
      :style="cardStyle"
      @click="handleCardClick"
    >
      <div class="web-promo-card__overlay">
        <span
          v-if="!hasImage"
          class="web-promo-card__motif"
          aria-hidden="true"
        >
          %
        </span>
        <div class="web-promo-card__content">
          <h3
            v-if="title"
            class="web-promo-card__title"
            :class="titleClass"
            :style="{ color: resolvedTextColor }"
          >
            {{ title }}
          </h3>
          <p
            v-if="text"
            class="web-promo-card__text"
            :class="textClass"
            :style="{ color: resolvedMutedTextColor }"
          >
            {{ text }}
          </p>
          <button
            v-if="showButton"
            type="button"
            class="btn web-promo-card__button"
            :class="buttonClass"
            @click.stop="handleLink"
          >
            {{ buttonText || "Ver más" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    css: {
      type: String,
      default: "",
    },
    cardCss: {
      type: String,
      default: "",
    },
    titleClass: {
      type: String,
      default: "",
    },
    textClass: {
      type: String,
      default: "",
    },
    buttonClass: {
      type: String,
      default: "",
    },
    title: {
      type: String,
      default: "",
    },
    text: {
      type: String,
      default: "",
    },
    buttonText: {
      type: String,
      default: "",
    },
    to: {
      type: String,
      default: "",
    },
    src: {
      type: String,
      default: "",
    },
    bgColor: {
      type: String,
      default: "",
    },
    linkMode: {
      type: String,
      default: "button",
    },
  },
  computed: {
    isCardClickable() {
      return !!this.to && this.linkMode === "card";
    },
    showButton() {
      return !!this.to && this.linkMode !== "card";
    },
    cardStyle() {
      const customColor = this.normalizeHexColor(this.bgColor);
      const backgroundColor = customColor || (this.hasImage ? "#20313f" : "#f9d5e5");
      const style = {
        backgroundColor,
        backgroundImage: this.hasImage ? `url(${this.src})` : "none",
        backgroundSize: this.hasImage ? "cover" : "auto",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      };
      if (this.hasImage && customColor) {
        const channels = this.getColorChannels(customColor);
        style["--promo-overlay-start"] = `rgba(${channels}, 0.92)`;
        style["--promo-overlay-middle"] = `rgba(${channels}, 0.78)`;
        style["--promo-overlay-end"] = `rgba(${channels}, 0.08)`;
      }
      return style;
    },
    hasImage() {
      return !!String(this.src || "").trim();
    },
    resolvedTextColor() {
      if (this.hasImage && !this.normalizeHexColor(this.bgColor)) return "#ffffff";
      return this.getReadableTextColor(this.bgColor || "#f9d5e5");
    },
    resolvedMutedTextColor() {
      if (this.hasImage && !this.normalizeHexColor(this.bgColor)) return "rgba(255, 255, 255, 0.92)";
      return this.getReadableTextColor(this.bgColor || "#f9d5e5", 0.86);
    },
  },
  methods: {
    normalizeHexColor(value) {
      const color = String(value || "").trim();
      if (/^#[0-9a-f]{6}$/i.test(color)) return color;
      if (/^#[0-9a-f]{3}$/i.test(color)) {
        return `#${color.slice(1).split("").map((digit) => digit + digit).join("")}`;
      }
      return "";
    },
    getColorChannels(color) {
      const hex = this.normalizeHexColor(color).slice(1);
      return [0, 2, 4].map((index) => parseInt(hex.slice(index, index + 2), 16)).join(", ");
    },
    getReadableTextColor(color = "", alpha = 1) {
      const normalized = this.normalizeHexColor(color).slice(1);
      if (!normalized) {
        return alpha === 1 ? "#1f2937" : "rgba(31, 41, 55, 0.86)";
      }

      const r = parseInt(normalized.slice(0, 2), 16);
      const g = parseInt(normalized.slice(2, 4), 16);
      const b = parseInt(normalized.slice(4, 6), 16);
      const luminance = (r * 299 + g * 587 + b * 114) / 1000;

      if (luminance > 170) {
        return alpha === 1 ? "#1f2937" : `rgba(31, 41, 55, ${alpha})`;
      }

      return alpha === 1 ? "#ffffff" : `rgba(255, 255, 255, ${alpha})`;
    },
    handleCardClick() {
      if (!this.isCardClickable) return;
      this.handleLink();
    },
    handleLink() {
      if (!this.to) return;
      if (this.to.indexOf("http") > -1) window.open(this.to, "_blank");
      else this.$router.push(this.getLink(this.to));
    },
  },
};
</script>

<style scoped>
.web-promo-card-wrapper {
  width: 100%;
}

.web-promo-card {
  width: 100%;
  min-height: 220px;
  isolation: isolate;
  border-radius: 24px;
  overflow: hidden;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.52);
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.12);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.web-promo-card--clickable:hover {
  transform: translateY(-3px);
  box-shadow: 0 20px 42px rgba(15, 23, 42, 0.18);
}

.web-promo-card--clickable {
  cursor: pointer;
}

.web-promo-card__overlay {
  width: 100%;
  min-height: 220px;
  display: flex;
  align-items: center;
  position: relative;
  padding: clamp(1.4rem, 3vw, 2.25rem);
  background: linear-gradient(
    115deg,
    rgba(255, 255, 255, 0.18) 0%,
    rgba(255, 255, 255, 0.04) 58%,
    rgba(15, 23, 42, 0.08) 100%
  );
}

.web-promo-card__motif {
  position: absolute;
  top: 50%;
  right: 5%;
  z-index: -1;
  color: rgba(255, 255, 255, 0.17);
  font-size: clamp(8rem, 17vw, 15rem);
  font-weight: 900;
  line-height: 0.7;
  transform: translateY(-50%) rotate(-12deg);
  user-select: none;
}

.web-promo-card--image .web-promo-card__overlay {
  background: linear-gradient(
    90deg,
    var(--promo-overlay-start, rgba(15, 23, 42, 0.72)) 0%,
    var(--promo-overlay-middle, rgba(15, 23, 42, 0.3)) 58%,
    var(--promo-overlay-end, rgba(15, 23, 42, 0.06)) 100%
  );
}

.web-promo-card__content {
  position: relative;
  z-index: 1;
  max-width: min(72%, 680px);
}

.web-promo-card__title {
  margin-bottom: 0.7rem;
  font-size: clamp(1.5rem, 2.7vw, 2.65rem);
  font-weight: 800;
  line-height: 1.02;
  letter-spacing: -0.035em;
  text-wrap: balance;
}

.web-promo-card__text {
  margin-bottom: 1.15rem;
  font-size: clamp(0.96rem, 1.35vw, 1.1rem);
  line-height: 1.5;
}

.web-promo-card__button {
  border: 0;
  border-radius: 999px;
  min-height: 44px;
  padding: 0.7rem 1.4rem;
  background: #ffffff;
  color: #111827;
  font-weight: 700;
  box-shadow: 0 10px 24px rgba(17, 24, 39, 0.12);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.web-promo-card__button:hover,
.web-promo-card__button:focus {
  color: #111827;
  box-shadow: 0 13px 28px rgba(17, 24, 39, 0.2);
  transform: translateY(-1px);
}

@media (max-width: 767px) {
  .web-promo-card,
  .web-promo-card__overlay {
    min-height: 180px;
  }

  .web-promo-card__content {
    max-width: 88%;
  }

  .web-promo-card__motif {
    right: -2%;
    font-size: 9rem;
  }

  .web-promo-card__title {
    font-size: clamp(1.35rem, 7vw, 2rem);
  }
}

@media (prefers-reduced-motion: reduce) {
  .web-promo-card,
  .web-promo-card__button {
    transition: none;
  }
}
</style>
